import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_URL } from '../config';

const SignUp = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('initial');
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    companyName: '',
  });
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleEmailSubmit = async () => {
    if (!formData.email) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/users/signup/send-code/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: formData.email }),
      });

      if (response.ok) {
        setStep('checkEmail');
      } else {
        const data = await response.json();
        setError(data.message || 'Error al verificar el email');
      }
    } catch (error) {
      setError('Error de conexión. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDetailsSubmit = async () => {
    if (!formData.name || !formData.companyName) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/users/signup/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStep('code');
      } else {
        const data = await response.json();
        setError(data.message || 'Error al crear la cuenta');
      }
    } catch (error) {
      setError('Error de conexión. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async () => {
    if (!code) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/users/signup/verify-code/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          code,
        }),
      });

      if (response.ok) {
        navigate('/app/dashboard');
      } else {
        const data = await response.json();
        setError(data.message || 'Código inválido');
      }
    } catch (error) {
      setError('Error de conexión. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const renderInitialStep = () => (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-center mb-8">
        Crear una cuenta en Fivia
      </h1>

      <button
        onClick={() => window.location.href = '/api/auth/google'}
        className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
      >
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        Continuar con Google
      </button>

      <button
        onClick={() => setStep('email')}
        className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
      >
        Continuar con email
      </button>

      <button
        onClick={() => window.location.href = '/api/auth/saml'}
        className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
      >
        Continuar con SAML SSO
      </button>

      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          ¿Ya tienes una cuenta?{' '}
          <Link to="/app/login" className="font-medium text-primary-600 hover:text-primary-500">
            Inicia sesión
          </Link>
          {' '}o{' '}
          <a href="/learn-more" className="font-medium text-primary-600 hover:text-primary-500">
            Conoce más
          </a>
        </p>
      </div>
    </div>
  );

  const renderCheckEmailStep = () => (
    <div className="space-y-8 text-center">
      <div className="flex flex-col items-center justify-center mt-8 mb-8">
        <div className="rounded-full bg-gray-200 w-16 h-16 flex items-center justify-center mb-6">
          <svg width="32" height="32" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#222" /></svg>
        </div>
        <h1 className="text-2xl font-semibold mb-2">Check your email</h1>
        <p className="text-gray-500 mb-6">
          We've sent you a temporary login link.<br />
          Please check your inbox at <span className="font-medium">{formData.email}</span>.
        </p>
        <button
          className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mb-4"
          onClick={() => setStep('code')}
        >
          Enter code manually
        </button>
        <button
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          onClick={() => navigate('/app/login')}
        >
          Back to login
        </button>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 'initial':
        return renderInitialStep();
      case 'email':
        return (
          <div className="space-y-6">
            <h1 className="text-2xl font-semibold text-center mb-8">
              Ingresa tu email
            </h1>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Correo electrónico
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="tu@empresa.com"
                />
              </div>
            </div>

            <div className="flex items-center justify-between space-x-4">
              <button
                type="button"
                onClick={() => setStep('initial')}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Atrás
              </button>
              <button
                type="button"
                onClick={handleEmailSubmit}
                disabled={isLoading || !formData.email}
                className={`flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  (isLoading || !formData.email) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? 'Verificando...' : 'Continuar'}
              </button>
            </div>
          </div>
        );

      case 'details':
        return (
          <form className="space-y-6" onSubmit={handleDetailsSubmit}>
            <h1 className="text-2xl font-semibold text-center mb-8">
              Información de la cuenta
            </h1>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Tu nombre
              </label>
              <div className="mt-1">
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="John Doe"
                />
              </div>
            </div>

            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                Nombre de la empresa
              </label>
              <div className="mt-1">
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  required
                  value={formData.companyName}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Acme Inc."
                />
              </div>
            </div>

            <div className="flex items-center justify-between space-x-4">
              <button
                type="button"
                onClick={() => setStep('email')}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Atrás
              </button>
              <button
                type="submit"
                disabled={isLoading || !formData.name || !formData.companyName}
                className={`flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  (isLoading || !formData.name || !formData.companyName) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? 'Creando...' : 'Crear cuenta'}
              </button>
            </div>
          </form>
        );

      case 'code':
        return (
          <form className="space-y-6" onSubmit={handleCodeSubmit}>
            <h1 className="text-2xl font-semibold text-center mb-8">
              Verifica tu email
            </h1>
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                  Código de verificación
                </label>
                <button
                  type="button"
                  onClick={() => setStep('email')}
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  Usar otro email
                </button>
              </div>
              <div className="mt-1">
                <input
                  id="code"
                  name="code"
                  type="text"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Ingresa el código"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Se ha enviado un código de verificación a {formData.email}
              </p>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading || !code}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  (isLoading || !code) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? 'Verificando...' : 'Verificar código'}
              </button>
            </div>
          </form>
        );

      case 'checkEmail':
        return renderCheckEmailStep();

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex flex-col items-center">
          <img
            className="h-12 w-auto"
            src="/logo.svg"
            alt="Fivia"
          />
        </Link>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-6 rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default SignUp; 